import { yupResolver } from '@hookform/resolvers/yup';
import { Close } from '@mui/icons-material';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import FormProvider, {
  RHFAutocomplete,
  RHFTextField,
} from 'src/components/hook-form';
import { CreateTemplateDtoTemplateTypeEnum } from 'woofwoof-api';
import * as Yup from 'yup';
import {
  categoryTranslations,
  templateTypeTranslations,
} from '../context/TemplatesContext';
import { useTemplatesContext } from '../hooks/useTemplatesContext';
import { translateKey } from 'src/locales/translate';


// External API values (with strings for specialization and location)
export type TemplatesFiltersFormValuesProps = {
  name?: string;
  category?: string;
  templateType?: CreateTemplateDtoTemplateTypeEnum;
};

interface TemplatesFiltersFormProps {
  isOpen: boolean;
}

export default function TemplatesFiltersForm({
  isOpen,
}: TemplatesFiltersFormProps) {
  const { templateCategories, isTemplateCategoriesLoading, setFilters } =
    useTemplatesContext();

  const categoriesOptions = templateCategories?.map((category) => ({
    value: category,
    label:
      categoryTranslations[category] ||
      category
        .toString()
        .split('_')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
  }));

  const templateTypeOptions = Object.values(
    CreateTemplateDtoTemplateTypeEnum,
  ).map((type) => ({
    value: type,
    label:
      templateTypeTranslations[type] ||
      type
        .toString()
        .split('_')
        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
  }));

  const SearchFiltersSchema: Yup.Schema<TemplatesFiltersFormValuesProps> =
    Yup.object().shape({
      name: Yup.string().optional(),
      category: Yup.string().optional(),
      templateType: Yup.string()
        .oneOf(
          Object.values(CreateTemplateDtoTemplateTypeEnum),
          translateKey("hardcoded.nieprawidlowy_typ_szablonu"),
        )
        .optional(),
    });

  const defaultValues: TemplatesFiltersFormValuesProps = {
    name: '',
    category: '',
    templateType: undefined,
  };

  const methods = useForm<TemplatesFiltersFormValuesProps>({
    resolver: yupResolver(SearchFiltersSchema),
    defaultValues,
    values: defaultValues,
  });

  const { setValue, watch } = methods;

  const nameValue = watch('name');
  const categoryValue = watch('category');
  const templateTypeValue = watch('templateType');

  const selectedCategoryOption = categoriesOptions?.find(
    (option: any) => option.value === categoryValue,
  );

  const selectedTemplateTypeOption = templateTypeOptions?.find(
    (option: any) => option.value === templateTypeValue,
  );

  const [isNameFieldHovered, setIsNameFieldHovered] = useState(false);
  const [isNameFieldFocused, setIsNameFieldFocused] = useState(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced filter update function
  const debouncedSetFilters = useCallback(
    (values: TemplatesFiltersFormValuesProps) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        setFilters(values);
      }, 350);
    },
    [setFilters],
  );

  // Auto-submit when form values change
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type === 'change') {
        debouncedSetFilters(value);
      }
    });
    return () => {
      subscription.unsubscribe();
      // Clean up timeout on unmount
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [watch, debouncedSetFilters]);

  const handleClear = () => {
    setValue('name', '');
    setValue('category', '');
    setValue('templateType', undefined);
    // Trigger update manually or let watch handle it if it detects setValue?
    // watch triggers on 'change' type mostly from user input, setValue might not trigger subscription with type 'change' depending on RHF version/config.
    // But we are observing all changes. If setValue doesn't trigger, we might need to call setFilters manually.
    // RHF watch usually triggers. Let's verify behavior. If not, we can call debouncedSetFilters(defaultValues).
    // Actually for safety:
    setFilters(defaultValues);
  };

  const renderActiveFilters = () => {
    const filters = [
      nameValue && {
        key: 'name',
        label: `Wyszukaj: ${nameValue}`,
        onDelete: () => setValue('name', ''),
      },
      categoryValue && {
        key: 'category',
        label: `Kategoria: ${
          categoriesOptions?.find((opt) => opt.value === categoryValue)
            ?.label || categoryValue
        }`,
        onDelete: () => setValue('category', ''),
      },
      templateTypeValue && {
        key: 'templateType',
        label: `Typ: ${
          templateTypeOptions?.find((opt) => opt.value === templateTypeValue)
            ?.label || templateTypeValue
        }`,
        onDelete: () => setValue('templateType', undefined),
      },
    ].filter(Boolean) as Array<{
      key: string;
      label: string;
      onDelete: () => void;
    }>;

    if (filters.length === 0) return null;

    return (
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        gap={1}
        sx={{ justifyContent: 'flex-end', px: 3, pb: 2 }}
      >
        {filters.map((filter) => (
          <Chip
            key={filter.key}
            label={filter.label}
            onDelete={filter.onDelete}
            size="small"
            color="primary"
            variant="outlined"
          />
        ))}
        <Chip
          label={translateKey("visit.filtersClearAll")}
          onClick={handleClear}
          size="small"
          color="default"
          variant="outlined"
          deleteIcon={<Close />}
          onDelete={handleClear}
        />
      </Stack>
    );
  };

  return (
    <FormProvider
      methods={methods}
      onSubmit={() => null}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
      }}
    >
      {renderActiveFilters()}
      <Collapse in={isOpen}>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            md: 'repeat(3, 1fr)',
          }}
          gap={2}
          sx={{ px: 3, pt: 1, pb: 3 }}
        >
          <RHFTextField
            name="name"
            label="Wyszukaj szablon"
            size="small"
            disabled={isTemplateCategoriesLoading}
            onMouseEnter={() => setIsNameFieldHovered(true)}
            onMouseLeave={() => setIsNameFieldHovered(false)}
            onFocus={() => setIsNameFieldFocused(true)}
            onBlur={() => setIsNameFieldFocused(false)}
            InputProps={{
              endAdornment:
                nameValue && (isNameFieldHovered || isNameFieldFocused) ? (
                  <InputAdornment position="end" sx={{ marginRight: '8px' }}>
                    <IconButton
                      sx={{
                        p: 0.5,
                        opacity:
                          isNameFieldHovered || isNameFieldFocused ? 1 : 0,
                        transition: 'opacity 0.2s ease-in-out',
                      }}
                      onClick={() => setValue('name', '')}
                      edge="end"
                      disabled={isTemplateCategoriesLoading}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />

          <RHFAutocomplete
            name="category"
            label={translateKey("search.ui.category")}
            size="small"
            disabled={isTemplateCategoriesLoading}
            freeSolo
            options={categoriesOptions || []}
            onChange={(event, newValue) => {
              if (newValue) {
                const formattedValue = newValue.value
                  .trim()
                  .toLowerCase()
                  .replace(/\s+/g, '_');

                return formattedValue;
              } else {
                setValue('category', '');
                return '';
              }
            }}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input' && newInputValue) {
                const formattedValue = newInputValue
                  .trim()
                  .toLowerCase()
                  .replace(/\s+/g, '_');
                if (formattedValue && formattedValue !== categoryValue) {
                  setValue('category', formattedValue);
                }
              }
            }}
            value={
              (selectedCategoryOption as any) || {
                value: categoryValue,
                label:
                  categoryTranslations[categoryValue || ''] ||
                  (categoryValue || '')
                    .toString()
                    .split('_')
                    .map(
                      (word: string) =>
                        word.charAt(0).toUpperCase() + word.slice(1),
                    )
                    .join(' '),
              }
            }
            getOptionLabel={(option) =>
              (
                option as {
                  value: string;
                  label: string;
                }
              ).label || ''
            }
            isOptionEqualToValue={(option, value) =>
              (
                option as {
                  value: string;
                  label: string;
                }
              ).value === (value as { value: string; label: string }).value
            }
          />

          <RHFAutocomplete
            name="templateType"
            label="Typ"
            size="small"
            disabled={isTemplateCategoriesLoading}
            freeSolo
            options={templateTypeOptions || []}
            onChange={(event, newValue) => {
              if (newValue) {
                setValue('templateType', newValue.value);
                return newValue.value;
              } else {
                setValue(
                  'templateType',
                  CreateTemplateDtoTemplateTypeEnum.General,
                );
                return '';
              }
            }}
            value={
              (selectedTemplateTypeOption as any) || {
                value: templateTypeValue,
                label:
                  templateTypeValue &&
                  (templateTypeTranslations[templateTypeValue] ||
                    templateTypeValue),
              }
            }
            getOptionLabel={(option) =>
              (
                option as {
                  value: string;
                  label: string;
                }
              ).label || ''
            }
            isOptionEqualToValue={(option, value) =>
              (
                option as {
                  value: string;
                  label: string;
                }
              ).value === (value as { value: string; label: string }).value
            }
          />
        </Box>
      </Collapse>
    </FormProvider>
  );
}
